import React, { useState, useRef } from 'react';
import { Container,Stepper,Step,StepLabel,Button,Typography,Paper,Box,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  IconButton, Chip,
  Alert,
  Snackbar,
  Card,
  CardContent,
  StepConnector,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { 
  ArrowBack,
  ArrowForward, 
  CheckCircle, 
  Close,
  AddPhotoAlternate,
  SportsTennis,
  SportsBasketball,
  SportsSoccer,
  SportsCricket,
  MonetizationOn,
  AccessTime,
  LocationOn,
  Description,
  Check,
  Info,
  ContactSupport
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import admin_api from '../../../api/admin';
import AuthModal from '../../AuthModal'; // Import the AuthModal
import { useAuth } from '../../../contexts/AuthContext'; // Import AuthContext
import SecureLS from 'secure-ls';

const ls = new SecureLS({ encodingType: 'aes', isCompression: false });

// Styled components for dark theme
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
  background: '#1a2235',
  color: '#ffffff',
}));

const StyledPageContainer = styled(Box)(({ theme }) => ({
  paddingTop: '100px',
  paddingBottom: '50px',
  minHeight: 'calc(100vh - 64px)',
  backgroundColor: '#12192c',
}));

const StyledStepConnector = styled(StepConnector)(({ theme }) => ({
  '& .MuiStepConnector-line': {
    borderColor: '#06b6d4',
    borderTopWidth: 3,
  },
}));

const StyledStepLabel = styled(StepLabel)(({ theme }) => ({
  '& .MuiStepLabel-label': {
    marginTop: theme.spacing(1),
    fontWeight: 500,
    color: '#e2e8f0',
  },
  '& .MuiStepLabel-label.Mui-active': {
    color: '#06b6d4',
    fontWeight: 700,
  },
  '& .MuiStepLabel-label.Mui-completed': {
    color: '#06b6d4',
    fontWeight: 700,
  },
}));

const StyledFormSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: '#1e293b',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.15)',
  color: '#ffffff',
}));

const ImagePreviewContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

const ImagePreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: 120,
  height: 120,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  border: '1px solid #2d3748',
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#2d3748',
    },
    '&:hover fieldset': {
      borderColor: '#4a5568',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#06b6d4',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#a0aec0',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#06b6d4',
  },
  '& .MuiOutlinedInput-input': {
    color: '#e2e8f0',
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: '#06b6d4',
  },
  '& .MuiFormHelperText-root': {
    color: '#a0aec0',
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: '#f56565',
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    color: '#a0aec0',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#06b6d4',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#2d3748',
    },
    '&:hover fieldset': {
      borderColor: '#4a5568',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#06b6d4',
    },
  },
  '& .MuiSelect-select': {
    color: '#e2e8f0',
  },
  '& .MuiSvgIcon-root': {
    color: '#a0aec0',
  },
  '& .MuiFormHelperText-root': {
    color: '#a0aec0',
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: '#2d3748',
  borderColor: '#4a5568',
  color: '#e2e8f0',
  '& .MuiCardContent-root': {
    padding: theme.spacing(2),
  },
}));

export default function RegisterCourt() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth(); // Use AuthContext
  const fileInputRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  
  // Authentication modal state
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(false);
  
  // Steps definition
  const steps = [
    {
      label: 'Basic Information',
      icon: <LocationOn color="primary" />,
      description: 'Enter the name and location of your court'
    },
    {
      label: 'Court Details',
      icon: <SportsTennis color="primary" />,
      description: 'Specify the court type and facilities'
    },
    {
      label: 'Pricing & Availability',
      icon: <MonetizationOn color="primary" />,
      description: 'Set prices and operating hours'
    },
    {
      label: 'Upload Images',
      icon: <AddPhotoAlternate color="primary" />,
      description: 'Add photos of your court'
    },
    {
      label: 'Review',
      icon: <CheckCircle color="primary" />,
      description: 'Verify your information'
    }
  ];

  const daysOfWeek = [
    'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
  ];

  const courtTypes = [
    { value: 'tennis', label: 'Tennis', icon: <SportsTennis /> },
    { value: 'basketball', label: 'Basketball', icon: <SportsBasketball /> },
    { value: 'volleyball', label: 'Volleyball', icon: <SportsSoccer /> },
    { value: 'badminton', label: 'Badminton', icon: <SportsCricket /> },
  ];

  const [formData, setFormData] = useState({
    courtName: '',
    location: '',
    courtType: '',
    description: '',
    facilities: [],
    hourly_rate: '',
    openTime: '08:00',
    closeTime: '22:00',
    availableDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'],
    maintenanceDay: '',
    images: []
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [previewImages, setPreviewImages] = useState([]);

  // Handle successful authentication
  const handleAuthSuccess = async (authData) => {
    if (authData?.token && authData?.user) {
    try {
      ls.set("token", authData.token);
      ls.set("user", JSON.stringify(authData.user)); // Ensure it's stringified
    } catch (e) {
      console.error("Failed to store auth token:", e);
    }
  }
    
    // If there was a pending submission, proceed with it
    if (pendingSubmission) {
      setPendingSubmission(false);
      // Small delay to ensure authentication state is properly set
      setTimeout(() => {
        handleSubmit();
      }, 500);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'hourly_rate') {
      const numericValue = value === '' ? '' : parseFloat(value);
      setFormData(prev => ({
        ...prev,
        [name]: numericValue || value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  
  const handleFacilityAdd = (facility) => {
    if (facility && !formData.facilities.includes(facility)) {
      setFormData(prev => ({
        ...prev,
        facilities: [...prev.facilities, facility]
      }));
    }
  };

  const handleFacilityDelete = (facility) => {
    setFormData(prev => ({
      ...prev,
      facilities: prev.facilities.filter(f => f !== facility)
    }));
  };

  const handleDayToggle = (day) => {
    const updatedDays = formData.availableDays.includes(day)
      ? formData.availableDays.filter(d => d !== day)
      : [...formData.availableDays, day];
    
    setFormData(prev => ({
      ...prev,
      availableDays: updatedDays
    }));
  };

  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const MAX_HEIGHT = 600;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > MAX_WIDTH) {
              height = height * (MAX_WIDTH / width);
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width = width * (MAX_HEIGHT / height);
              height = MAX_HEIGHT;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          resolve(dataUrl);
        };
        
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
      };
      
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
    });
  };

  const MAX_TOTAL_IMAGES = 4;

  const handleImageUpload = async (e) => {
    try {
      setLoading(true);

      const files = Array.from(e.target.files);
      if (files.length === 0) return;

      const remainingSlots = MAX_TOTAL_IMAGES - formData.images.length;

      if (remainingSlots <= 0) {
        alert(`You can only upload up to ${MAX_TOTAL_IMAGES} images.`);
        return;
      }

      const filesToProcess = files.slice(0, remainingSlots);

      if (files.length > remainingSlots) {
        alert(`You selected ${files.length} images. Only the first ${remainingSlots} will be used.`);
      }

      for (const file of filesToProcess) {
        try {
          const compressedImage = await compressImage(file);

          setPreviewImages(prev => [...prev, compressedImage]);

          setFormData(prev => ({
            ...prev,
            images: [...prev.images, compressedImage]
          }));
        } catch (err) {
          console.error('Error processing image:', err);
        }
      }
    } catch (error) {
      console.error('Image upload error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveImage = (index) => {
    setPreviewImages(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const validateStep = (step) => {
    const errors = {};
    
    switch (step) {
      case 0:
        if (!formData.courtName.trim()) errors.courtName = 'Court name is required';
        if (!formData.location.trim()) errors.location = 'Location is required';
        break;
      case 1:
        if (!formData.courtType) errors.courtType = 'Court type is required';
        if (!formData.description.trim()) errors.description = 'Description is required';
        break;
      case 2:
        if (!formData.hourly_rate) errors.hourly_rate = 'Price is required';
        else if (isNaN(parseFloat(formData.hourly_rate))) errors.hourly_rate = 'Price must be a number';
        if (!formData.openTime) errors.openTime = 'Opening time is required';
        if (!formData.closeTime) errors.closeTime = 'Closing time is required';
        if (formData.availableDays.length === 0) errors.availableDays = 'At least one available day is required';
        break;
      case 3:
        if (formData.images.length === 0) errors.images = 'At least one image is required';
        break;
      default:
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Enhanced submit function with authentication check
  const handleSubmit = async () => {
    // Check authentication before proceeding
    if (!isAuthenticated) {
      setPendingSubmission(true);
      setAuthModalOpen(true);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      if (isNaN(parseFloat(formData.hourly_rate))) {
        setError('Hourly rate must be a valid number');
        setLoading(false);
        return;
      }
      
      const preparedImages = formData.images.length > 0
        ? (Array.isArray(previewImages) ? previewImages : []).map((src, i) => 
            typeof src === 'string' ? src : null).filter(Boolean)
        : [];
        
      const apiData = {
        courtName: formData.courtName,
        location: formData.location,
        description: formData.description,
        hourly_rate: parseFloat(formData.hourly_rate),
        openTime: formData.openTime,
        closeTime: formData.closeTime,
        courtType: formData.courtType,
        facilities: formData.facilities,
        availableDays: formData.availableDays,
        maintenanceDay: formData.maintenanceDay,
        images: preparedImages,
        images_count: formData.images.length
      };
      const response = await admin_api.create_court(apiData);
      
      if (response && response.data && response.data.success) {
        setSuccess(true);
        setSubmissionComplete(true);
        
        setTimeout(() => {
          navigate('/admin/manage-court', { 
            state: { success: true, message: 'Court registered successfully!' }
          });
        }, 2000);
      } else {
        const errorMessage = response?.data?.message || 'Failed to register court. Please try again.';
        setError(errorMessage);
      }
    } catch (err) {
      console.error('Court registration error:', err);
      setError(err.message || 'Failed to register court. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const textFieldProps = {
    variant: "outlined",
    fullWidth: true,
    sx: { mb: 2 }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0: // Basic Information
        return (
          <StyledFormSection>
            <Typography variant="h6" gutterBottom sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
              Basic Court Information
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#a0aec0' }}>
              Enter the basic details about your court. This information will be displayed to users looking to book courts.
            </Typography>
            
            <StyledTextField
              {...textFieldProps}
              label="Court Name"
              name="courtName"
              value={formData.courtName}
              onChange={handleInputChange}
              error={!!validationErrors.courtName}
              helperText={validationErrors.courtName || "Enter a clear, descriptive name for your court"}
              placeholder="e.g., Central Tennis Court"
              InputProps={{
                startAdornment: (
                  <SportsTennis color="primary" sx={{ mr: 1 }} />
                ),
              }}
            />
            
            <StyledTextField
              {...textFieldProps}
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleInputChange}
              error={!!validationErrors.location}
              helperText={validationErrors.location || "Provide the full address of your court"}
              placeholder="e.g., 123 Sports Avenue, City, Country"
              InputProps={{
                startAdornment: (
                  <LocationOn color="primary" sx={{ mr: 1 }} />
                ),
              }}
            />
          </StyledFormSection>
        );

      case 1: // Court Details
        return (
          <StyledFormSection>
            <Typography variant="h6" gutterBottom sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
              Court Details
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#a0aec0' }}>
              Provide more specific information about your court, including type and available facilities.
            </Typography>
            
            <StyledFormControl fullWidth error={!!validationErrors.courtType} sx={{ mb: 3 }}>
              <InputLabel>Court Type</InputLabel>
              <Select
                name="courtType"
                value={formData.courtType}
                onChange={handleInputChange}
                startAdornment={formData.courtType ? 
                  courtTypes.find(c => c.value === formData.courtType)?.icon : null}
              >
                {courtTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {type.icon}
                      <Box sx={{ ml: 1 }}>{type.label}</Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {validationErrors.courtType && (
                <FormHelperText error>{validationErrors.courtType}</FormHelperText>
              )}
            </StyledFormControl>
            
            <StyledTextField
              {...textFieldProps}
              multiline
              rows={4}
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              error={!!validationErrors.description}
              helperText={validationErrors.description || "Describe your court, including surface type, dimensions, etc."}
              InputProps={{
                startAdornment: (
                  <Description color="primary" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }} />
                ),
              }}
            />
            
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, color: '#e2e8f0' }}>
              Facilities
            </Typography>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {formData.facilities.map((facility) => (
                <Chip
                  key={facility}
                  label={facility}
                  onDelete={() => handleFacilityDelete(facility)}
                  sx={{ 
                    backgroundColor: 'rgba(6, 182, 212, 0.1)',
                    color: '#06b6d4', 
                    borderColor: '#06b6d4',
                    '&:hover': { borderColor: '#0891b2', backgroundColor: 'rgba(6, 182, 212, 0.15)' }
                  }}
                />
              ))}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <StyledTextField
                label="Add Facility"
                placeholder="e.g., Changing rooms"
                variant="outlined"
                size="small"
                id="new-facility"
                sx={{ flex: 1 }}
              />
              <Button 
                variant="contained" 
                onClick={() => {
                  const input = document.getElementById('new-facility');
                  handleFacilityAdd(input.value);
                  input.value = '';
                }}
                sx={{
                  backgroundColor: '#06b6d4',
                  '&:hover': { backgroundColor: '#0891b2' },
                }}
              >
                Add
              </Button>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ color: '#a0aec0' }}>
                Common facilities: Changing rooms, Showers, Equipment rental, Lighting, Spectator seating
              </Typography>
            </Box>
          </StyledFormSection>
        );

      case 2: // Pricing & Availability
        return (
          <StyledFormSection>
            <Typography variant="h6" gutterBottom sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
              Pricing & Availability
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#a0aec0' }}>
              Set your court's operating hours, pricing, and availability throughout the week.
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  {...textFieldProps}
                  type="number"
                  label="Price per Hour ($)"
                  name="hourly_rate"
                  value={formData.hourly_rate}
                  onChange={handleInputChange}
                  error={!!validationErrors.hourly_rate}
                  helperText={validationErrors.hourly_rate}
                  InputProps={{
                    startAdornment: (
                      <MonetizationOn color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <StyledFormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Maintenance Day</InputLabel>
                  <Select
                    name="maintenanceDay"
                    value={formData.maintenanceDay}
                    onChange={handleInputChange}
                  >
                    <MenuItem value="">None</MenuItem>
                    {daysOfWeek.map((day) => (
                      <MenuItem key={day.toLowerCase()} value={day.toLowerCase()}>
                        {day}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText style={{ color: '#a0aec0' }}>
                    Select a day when the court is closed for maintenance (optional)
                  </FormHelperText>
                </StyledFormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  type="time"
                  label="Opening Time"
                  name="openTime"
                  value={formData.openTime}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!validationErrors.openTime}
                  helperText={validationErrors.openTime}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <AccessTime color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  type="time"
                  label="Closing Time"
                  name="closeTime"
                  value={formData.closeTime}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                  error={!!validationErrors.closeTime}
                  helperText={validationErrors.closeTime}
                  sx={{ mb: 2 }}
                  InputProps={{
                    startAdornment: (
                      <AccessTime color="primary" sx={{ mr: 1 }} />
                    ),
                  }}
                />
              </Grid>
            </Grid>
            
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, color: '#e2e8f0' }}>
              Available Days
            </Typography>
            
            {!!validationErrors.availableDays && (
              <FormHelperText error>{validationErrors.availableDays}</FormHelperText>
            )}
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {daysOfWeek.map((day) => {
                const dayLower = day.toLowerCase();
                const isSelected = formData.availableDays.includes(dayLower);
                const isMaintenanceDay = formData.maintenanceDay === dayLower;
                
                return (
                  <Chip
                    key={day}
                    label={day}
                    onClick={() => !isMaintenanceDay && handleDayToggle(dayLower)}
                    color={isSelected ? "primary" : "default"}
                    variant={isSelected ? "filled" : "outlined"}
                    disabled={isMaintenanceDay}
                    sx={{ 
                      opacity: isMaintenanceDay ? 0.5 : 1,
                      cursor: isMaintenanceDay ? 'not-allowed' : 'pointer',
                      backgroundColor: isSelected ? 'rgba(6, 182, 212, 0.8)' : 'transparent',
                      color: isSelected ? '#ffffff' : '#e2e8f0',
                      borderColor: isSelected ? '#06b6d4' : '#4a5568',
                    }}
                  />
                );
              })}
            </Box>
            
            {formData.maintenanceDay && (
              <Alert 
                severity="info" 
                sx={{ 
                  mt: 2,
                  backgroundColor: 'rgba(6, 182, 212, 0.1)',
                  color: '#e2e8f0',
                  '& .MuiAlert-icon': {
                    color: '#06b6d4'
                  }
                }}
              >
                {daysOfWeek.find(d => d.toLowerCase() === formData.maintenanceDay)} is set as maintenance day and will not be available for booking.
              </Alert>
            )}
          </StyledFormSection>
        );
        
        case 3: // Upload Images
          return (
            <StyledFormSection>
              <Typography variant="h6" gutterBottom sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
                Upload Court Images
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#a0aec0' }}>
                Upload high-quality images of your court. Good photos increase booking rates.
              </Typography>

              <Box 
                sx={{ 
                  border: '2px dashed #4a5568', 
                  borderRadius: 2, 
                  p: 3, 
                  textAlign: 'center',
                  mb: 3,
                  cursor: previewImages.length >= 4 ? 'not-allowed' : 'pointer',
                  backgroundColor: previewImages.length >= 4 ? 'rgba(71, 85, 105, 0.5)' : 'rgba(6, 182, 212, 0.05)',
                  '&:hover': previewImages.length >= 4 ? {} : { 
                    backgroundColor: 'rgba(6, 182, 212, 0.1)',
                    borderColor: '#06b6d4'
                  },
                  opacity: previewImages.length >= 4 ? 0.6 : 1
                }}
                onClick={() => {
                  if (previewImages.length < 4) fileInputRef.current.click();
                }}
              >
                <AddPhotoAlternate sx={{ fontSize: 40, color: '#06b6d4', mb: 1 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ color: '#e2e8f0' }}>
                  {previewImages.length >= 4
                    ? 'Maximum of 4 images uploaded'
                    : 'Click to Upload Images'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#a0aec0' }}>
                  Recommended: Multiple angles, well-lit, showing the entire court
                </Typography>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  ref={fileInputRef}
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                  disabled={previewImages.length >= 4}
                />
              </Box>

              {previewImages.length >= 4 && (
                <Alert 
                  severity="info" 
                  sx={{ 
                    mb: 2,
                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                    color: '#3B82F6',
                    border: '1px solid rgba(59, 130, 246, 0.3)'
                  }}
                >
                  You can upload a maximum of 4 images only.
                </Alert>
              )}

              {validationErrors.images && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 2,
                    backgroundColor: 'rgba(244, 63, 94, 0.1)',
                    color: '#f43f5e'
                  }}
                >
                  {validationErrors.images}
                </Alert>
              )}

              <ImagePreviewContainer>
                {previewImages.map((src, index) => (
                  <ImagePreview key={index}>
                    <img 
                      src={src} 
                      alt={`Court ${index + 1}`} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveImage(index)}
                      sx={{
                        position: 'absolute',
                        top: 5,
                        right: 5,
                        bgcolor: 'rgba(0,0,0,0.5)',
                        color: '#fff',
                        '&:hover': { bgcolor: 'rgba(244, 63, 94, 0.8)' }
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </ImagePreview>
                ))}
              </ImagePreviewContainer>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ color: '#a0aec0' }}>
                  Tips: Upload at least 3 images showing different angles of your court.
                </Typography>
              </Box>
            </StyledFormSection>
          );

      case 4: // Review
        return (
          <StyledFormSection>
            <Typography variant="h6" gutterBottom sx={{ color: '#06b6d4', fontWeight: 'bold' }}>
              Review Your Court Information
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, color: '#a0aec0' }}>
              Please verify all details before submitting. You can go back to edit any section.
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <StyledCard variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#06b6d4', mb: 1 }}>
                      Basic Information
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <SportsTennis sx={{ mr: 1, color: '#06b6d4' }} />
                      <Typography sx={{ color: '#e2e8f0' }}><strong>Court Name:</strong> {formData.courtName}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LocationOn sx={{ mr: 1, color: '#06b6d4' }} />
                      <Typography sx={{ color: '#e2e8f0' }}><strong>Location:</strong> {formData.location}</Typography>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <StyledCard variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#06b6d4', mb: 1 }}>
                      Court Type & Pricing
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      {formData.courtType && courtTypes.find(c => c.value === formData.courtType)?.icon}
                      <Typography sx={{ ml: 1, color: '#e2e8f0' }}><strong>Court Type:</strong> {
                        formData.courtType ? (formData.courtType.charAt(0).toUpperCase() + formData.courtType.slice(1)) : ''
                      }</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <MonetizationOn sx={{ mr: 1, color: '#06b6d4' }} />
                      <Typography sx={{ color: '#e2e8f0' }}><strong>Price per Hour:</strong> ${formData.hourly_rate}</Typography>
                    </Box>
                  </CardContent>
                </StyledCard>
              </Grid>
              
              <Grid item xs={12}>
                <StyledCard variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#06b6d4', mb: 1 }}>
                      Description
                    </Typography>
                    <Typography sx={{ color: '#e2e8f0' }}>{formData.description}</Typography>
                  </CardContent>
                </StyledCard>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <StyledCard variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#06b6d4', mb: 1 }}>
                      Availability
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <AccessTime sx={{ mr: 1, color: '#06b6d4' }} />
                      <Typography sx={{ color: '#e2e8f0' }}><strong>Operating Hours:</strong> {formData.openTime} - {formData.closeTime}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                      <Info sx={{ mr: 1, color: '#06b6d4', mt: 0.5 }} />
                      <Box>
                        <Typography sx={{ color: '#e2e8f0' }}><strong>Available Days:</strong></Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {formData.availableDays.map(day => (
                            <Chip 
                              key={day} 
                              label={day.charAt(0).toUpperCase() + day.slice(1)} 
                              size="small" 
                              sx={{
                                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                                color: '#06b6d4',
                                borderColor: '#06b6d4'
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                    {formData.maintenanceDay && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Info sx={{ mr: 1, color: '#f59e0b' }} />
                        <Typography sx={{ color: '#f59e0b' }}>
                          <strong>Maintenance Day:</strong> {
                            formData.maintenanceDay.charAt(0).toUpperCase() + formData.maintenanceDay.slice(1)
                          }
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </StyledCard>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <StyledCard variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#06b6d4', mb: 1 }}>
                      Facilities
                    </Typography>
                    {formData.facilities.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {formData.facilities.map(facility => (
                          <Chip 
                            key={facility} 
                            label={facility} 
                            size="small"
                            icon={<Check />}
                            sx={{
                              backgroundColor: 'rgba(6, 182, 212, 0.1)',
                              color: '#06b6d4',
                              '& .MuiChip-icon': {
                                color: '#06b6d4'
                              }
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ color: '#a0aec0' }}>No facilities specified</Typography>
                    )}
                  </CardContent>
                </StyledCard>
              </Grid>
              
              <Grid item xs={12}>
                <StyledCard variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#06b6d4', mb: 1 }}>
                      Images ({formData.images.length})
                    </Typography>
                    <ImagePreviewContainer>
                      {previewImages.map((src, index) => (
                        <ImagePreview key={index}>
                          <img 
                            src={src} 
                            alt={`Court ${index + 1}`} 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </ImagePreview>
                      ))}
                    </ImagePreviewContainer>
                  </CardContent>
                </StyledCard>
              </Grid>
            </Grid>
          </StyledFormSection>
        );
      
      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <StyledPageContainer>
      <Container maxWidth="lg">
        <StyledPaper>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <IconButton 
              sx={{ mr: 2, color: '#e2e8f0' }}
              onClick={() => setCancelDialogOpen(true)}
            >
              <ArrowBack />
            </IconButton>
            <Typography component="h1" variant="h4" fontWeight="bold" color="#06b6d4">
              Register Your Court
            </Typography>
          </Box>
          
          {/* Authentication status indicator */}
          {!isAuthenticated && (
            <Alert 
              severity="info" 
              sx={{ 
                mb: 3,
                backgroundColor: 'rgba(6, 182, 212, 0.1)',
                color: '#e2e8f0',
                '& .MuiAlert-icon': {
                  color: '#06b6d4'
                }
              }}
            >
              You'll need to sign in or create an account before submitting your court registration.
            </Alert>
          )}
          
          <Snackbar 
            open={success} 
            autoHideDuration={4000} 
            onClose={() => setSuccess(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity="success" variant="filled" sx={{ width: '100%', backgroundColor: '#06b6d4' }}>
              Court registered successfully!
            </Alert>
          </Snackbar>
          
          <Stepper 
            activeStep={activeStep} 
            alternativeLabel 
            sx={{ mb: 4 }}
            connector={<StyledStepConnector />}
          >
            {steps.map((step, index) => (
              <Step key={step.label}>
                <Tooltip title={step.description} arrow placement="top">
                  <StyledStepLabel 
                    StepIconProps={{ 
                      icon: index < activeStep ? <CheckCircle color="primary" /> : step.icon 
                    }}
                  >
                    {step.label}
                  </StyledStepLabel>
                </Tooltip>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                backgroundColor: 'rgba(244, 63, 94, 0.1)',
                color: '#f43f5e',
                '& .MuiAlert-icon': {
                  color: '#f43f5e'
                }
              }}
            >
              {error}
            </Alert>
          )}

          <Box>
            {getStepContent(activeStep)}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              {activeStep !== 0 ? (
                <Button 
                  onClick={handleBack} 
                  startIcon={<ArrowBack />}
                  variant="outlined"
                  sx={{
                    color: '#e2e8f0',
                    borderColor: '#4a5568',
                    '&:hover': {
                      borderColor: '#a0aec0',
                      backgroundColor: 'rgba(160, 174, 192, 0.1)'
                    }
                  }}
                >
                  Back
                </Button>
              ) : (
                <Button 
                  onClick={() => setCancelDialogOpen(true)} 
                  variant="outlined"
                  sx={{
                    color: '#e2e8f0',
                    borderColor: '#4a5568',
                    '&:hover': {
                      borderColor: '#a0aec0',
                      backgroundColor: 'rgba(160, 174, 192, 0.1)'
                    }
                  }}
                >
                  Cancel
                </Button>
              )}
              
              {activeStep === steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading}
                  endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircle />}
                  size="large"
                  sx={{
                    backgroundColor: '#06b6d4',
                    '&:hover': { backgroundColor: '#0891b2' },
                  }}
                >
                  {loading ? 'Submitting...' : (isAuthenticated ? 'Submit Court' : 'Sign In & Submit')}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  endIcon={<ArrowForward />}
                  size="large"
                  sx={{
                    backgroundColor: '#06b6d4',
                    '&:hover': { backgroundColor: '#0891b2' },
                  }}
                >
                  Continue
                </Button>
              )}
            </Box>
          </Box>
          
          {/* Help and Support */}
          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #2a3449' }}>
            <Typography variant="subtitle2" color="#a0aec0" gutterBottom>
              Need help with court registration?
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button 
                variant="text" 
                size="small" 
                startIcon={<Info />}
                sx={{ color: '#a0aec0', '&:hover': { color: '#06b6d4' } }}
                onClick={() => window.open('/help/court-registration', '_blank')}
              >
                Registration Guide
              </Button>
              <Button 
                variant="text" 
                size="small" 
                startIcon={<ContactSupport />}
                sx={{ color: '#a0aec0', '&:hover': { color: '#06b6d4' } }}
                onClick={() => window.open('/contact-support', '_blank')}
              >
                Contact Support
              </Button>
            </Box>
          </Box>
        </StyledPaper>
      </Container>
      
      {/* Authentication Modal */}
      <AuthModal
        open={authModalOpen}
        onClose={() => {
          setAuthModalOpen(false);
          setPendingSubmission(false);
        }}
        onSuccess={handleAuthSuccess}
        defaultRole="admin"
        title="Court Administrator Registration"
        subtitle="Create an account or sign in to register your court. You'll be set up as a court administrator."
        showRoleInfo={true}
      />
      
      {/* Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        PaperProps={{
          style: {
            borderRadius: '8px',
            backgroundColor: '#1e293b'
          }
        }}
      >
        <DialogTitle sx={{ color: '#06b6d4' }}>Discard Changes?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#e2e8f0' }}>
            Are you sure you want to leave? All your progress will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setCancelDialogOpen(false)}
            sx={{ color: '#a0aec0' }}
          >
            Continue Editing
          </Button>
          <Button 
            onClick={() => navigate(-1)} 
            sx={{ 
              backgroundColor: '#f43f5e', 
              color: '#ffffff',
              '&:hover': { backgroundColor: '#e11d48' } 
            }}  
            variant="contained"
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Success completion dialog */}
      <Dialog
        open={submissionComplete}
        PaperProps={{
          style: {
            borderRadius: '8px',
            backgroundColor: '#1e293b'
          }
        }}
      >
        <DialogTitle sx={{ bgcolor: '#06b6d4', color: 'white', pb: 2 }}>
          <CheckCircle sx={{ mr: 1, verticalAlign: 'middle' }} />
          Court Registration Complete!
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText sx={{ color: '#e2e8f0' }} gutterBottom>
            Your court has been successfully registered and is now pending approval from our team. 
            We'll review your submission within 24-48 hours.
          </DialogContentText>
          <DialogContentText variant="body2" sx={{ color: '#a0aec0' }}>
            You'll receive an email notification once your court is approved and published.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => navigate('/admin/dashboard')}
            variant="outlined"
            sx={{
              color: '#e2e8f0',
              borderColor: '#4a5568',
              '&:hover': {
                borderColor: '#06b6d4',
                backgroundColor: 'rgba(6, 182, 212, 0.1)'
              }
            }}
          >
            Go to Dashboard
          </Button>
          <Button 
            onClick={() => {
              setSubmissionComplete(false);
              navigate('/admin/manage-court');
            }}
            variant="contained"
            sx={{
              backgroundColor: '#06b6d4',
              '&:hover': { backgroundColor: '#0891b2' },
            }}
          >
            View My Courts
          </Button>
        </DialogActions>
      </Dialog>
    </StyledPageContainer>
  );
}